/**
 * The ground behind a signed out hero, and the slot the founder video mounts
 * into.
 *
 * WITH NO PROPS it renders one very soft radial wash and nothing else. Screen
 * spec S2 asks for exactly that behind the auth card, and both graduation
 * screens want the same ground so every signed out screen reads as one
 * product. That path is unchanged and costs nothing.
 *
 * WITH A POSTER, and optionally a video, it becomes the dimmed founder loop.
 * That is why this was always a component with a video slot rather than a
 * background rule in a stylesheet: the footage landed, and the change was two
 * props at one call site and a file in public/media, with no layout moved.
 *
 * The rules that mounting had to keep, written down before the footage
 * existed, and kept:
 *
 *   1. It is decoration. aria-hidden, no focus stop, no controls, muted,
 *      playsInline, loop, and it never carries meaning a sighted person gets
 *      and a screen reader user does not.
 *   2. It plays once the hero sequence has settled, never under it. The
 *      doctrine allows one thing moving per screen, and while the address bar
 *      is typing, that one thing is the address bar. The caller enforces this
 *      by withholding videoSrc until then, which also keeps the request off
 *      the critical path.
 *   3. prefers-reduced-motion renders the poster frame and never the video.
 *      Save-Data does the same, and so does every phone, because this is a
 *      performance claim as much as a design one: a landing page for a cloud
 *      product that is slow on a Thai 4G phone is an argument against itself.
 *      Storyboard section 6 puts mobile on the still for the same reason.
 *   4. It stays under the dim layer. Text on this page always sits on solid
 *      ground, never on moving imagery.
 *
 * The poster is a plain img rather than the video element's own poster
 * attribute. The attribute would tie the still to a video that, on three of
 * the four paths above, never mounts at all.
 */

import { Suspense, lazy, useEffect, useState } from 'react';

import '../../styles/components/hero-backdrop.css';

// A separate chunk, fetched only when a video actually mounts. The console
// never mounts one, so the console never pays for the player.
const HeroVideo = lazy(() => import('./HeroVideo'));

export interface HeroBackdropProps {
  /**
   * The founder loop as mp4. Nothing moves until this is passed, which is what
   * made the slot safe to ship empty for as long as it was empty.
   */
  videoSrc?: string;
  /** The same loop as webm, offered first where the browser takes it. */
  videoWebmSrc?: string;
  /** The frame shown before playback, and instead of it wherever it is stilled. */
  posterSrc?: string;
  /** How far the wash reaches. The auth card wants a tighter one than the hero. */
  reach?: 'tight' | 'wide';
}

function query(feature: string): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(feature).matches;
}

/**
 * Three gates, one still. The same shape WaveField and CloudCorridor use, and
 * deliberately the same answer: there is no second art direction to maintain,
 * so every reason to stop moving lands on the poster.
 */
function preferStill(): boolean {
  if (typeof window === 'undefined') return true;
  if (query('(prefers-reduced-motion: reduce)')) return true;
  // Mobile, by both of the tests the rest of the page already trusts: a touch
  // device, and a viewport narrow enough that the storyboard puts it on the
  // fallback frame regardless.
  if (query('(pointer: coarse)')) return true;
  if (query('(max-width: 720px)')) return true;
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  if (nav.connection?.saveData) return true;
  return false;
}

export function HeroBackdrop({ videoSrc, videoWebmSrc, posterSrc, reach = 'wide' }: HeroBackdropProps) {
  const [still, setStill] = useState(true);

  // Resolved after mount, not during render, so the first paint is the poster
  // on every path and the video is only ever an addition to a complete
  // picture. The listener is there so somebody toggling the OS setting, or
  // turning a tablet, does not have to reload the page to see it obeyed.
  useEffect(() => {
    const resolve = () => setStill(preferStill());
    resolve();
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const width = window.matchMedia('(max-width: 720px)');
    motion.addEventListener('change', resolve);
    width.addEventListener('change', resolve);
    return () => {
      motion.removeEventListener('change', resolve);
      width.removeEventListener('change', resolve);
    };
  }, []);

  const hasMedia = Boolean(posterSrc || videoSrc);
  const showVideo = Boolean(videoSrc) && !still;

  return (
    <div
      className="hbd"
      data-reach={reach}
      data-media={hasMedia ? 'video' : 'none'}
      data-slot="founder-video"
      aria-hidden="true"
    >
      <span className="hbd__wash" />

      {posterSrc ? (
        <img className="hbd__poster" src={posterSrc} alt="" loading="lazy" decoding="async" />
      ) : null}

      {showVideo && videoSrc ? (
        // The poster is already painted underneath, so the fallback for the
        // chunk arriving is nothing at all rather than a hole.
        <Suspense fallback={null}>
          <HeroVideo src={videoSrc} webmSrc={videoWebmSrc} poster={posterSrc} />
        </Suspense>
      ) : null}

      {/* The dim layer exists to hold imagery down. With no imagery there is
          nothing to hold down, and painting it anyway would darken the ground
          the card sits on. */}
      {hasMedia ? <span className="hbd__dim" /> : null}
    </div>
  );
}
